/**
 * Structured Data (JSON-LD / schema.org) builders
 * Centralizowane generowanie schematów SEO wstrzykiwanych do <head>.
 */
import { base44 } from '@/api/base44Client';
import { getSeoConfig } from './seoConfig';

const SITE = 'https://lykke.info';
const LOGO = 'https://media.base44.com/images/public/69db395aa041ab4d87ab84d7/f42f4e4be_LYKKE_Nomad_1Colorrgb1920x.png';

// Mapa segment ścieżki -> { model, nazwa }
const SAUNA_MODELS = {
  umi: { model: 'UMI', name: 'LYKKE UMI badstue' },
  hiro: { model: 'HIRO', name: 'LYKKE HIRO badstue' },
  yori: { model: 'YORI', name: 'LYKKE YORI badstue' },
  tori: { model: 'TORI', name: 'LYKKE TORI badstue' },
  mori: { model: 'MORI', name: 'LYKKE MORI badstue' },
  trysil: { model: 'TRYSIL', name: 'LYKKE TRYSIL badstue' },
  verbier: { model: 'VERBIER', name: 'LYKKE VERBIER badstue' },
  aspen: { model: 'ASPEN', name: 'LYKKE ASPEN badstue' },
  dolomiti: { model: 'DOLOMITI', name: 'LYKKE DOLOMITI badstue' },
  chamonix: { model: 'CHAMONIX', name: 'LYKKE CHAMONIX badstue' },
  cortina: { model: 'CORTINA', name: 'LYKKE CORTINA badstue' },
  geilo: { model: 'GEILO', name: 'LYKKE GEILO badstue' },
  vikka: { model: 'VIKKA', name: 'LYKKE VIKKA badstue' },
  nomad: { model: 'NOMAD', name: 'LYKKE NOMAD badstue' },
  hytte: { model: 'HYTTE', name: 'LYKKE HYTTE badstue' },
};

const COLLECTION_NAMES = {
  '/sauna-ly': 'LYKKE LY',
  '/sauna-panorama': 'LYKKE PANORAMA',
  '/sauna-fjel': 'LYKKE FJELL',
};

const STATIC_BREADCRUMBS = {
  '/om-oss': 'Om oss',
  '/process': 'Prosess',
  '/portfolio': 'Portfolio',
  '/be-om-tilbud': 'Be om tilbud',
  '/sertifikater': 'Sertifikater',
  '/laer-mer-handverk': 'Lær mer',
  '/kebony-vedlikehold': 'Kebony vedlikehold',
  '/handverket': 'Håndverket',
};

/**
 * Zwraca info o modelu sauny dla stron produktowych/konfiguratorów.
 * Pomija strony kolekcji (ly/panorama/fjel).
 */
export function getSaunaModel(pathname) {
  const m = pathname.match(/^\/sauna-([a-z]+)/);
  if (!m) return null;
  const seg = m[1];
  if (seg === 'ly' || seg === 'panorama' || seg === 'fjel') return null;
  return SAUNA_MODELS[seg] || null;
}

/**
 * BreadcrumbList dla bieżącej ścieżki.
 */
export function getBreadcrumbs(pathname) {
  if (pathname === '/') return null;
  const crumbs = [{ name: 'Hjem', url: SITE + '/' }];
  const push = (name, path) => crumbs.push({ name, url: SITE + (path || pathname) });

  const info = getSaunaModel(pathname);
  if (info) {
    push('Badstuer', '/sauna-ly');
    push(info.name.replace(' badstue', ''), pathname);
  } else if (COLLECTION_NAMES[pathname]) {
    push('Badstuer', '/sauna-ly');
    push(COLLECTION_NAMES[pathname], pathname);
  } else if (STATIC_BREADCRUMBS[pathname]) {
    push(STATIC_BREADCRUMBS[pathname], pathname);
  } else {
    return null; // nieznane / admin — bez okruszków
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((c, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: c.name,
      item: c.url,
    })),
  };
}

/**
 * FAQPage dla strony /process (proces zamawiania).
 */
export function getFaqSchema() {
  const qa = [
    {
      q: 'Hvor lang tid tar levering av badstuen?',
      a: 'Standard leveringstid er 6–10 uker fra bekreftet ordre. Tilpasninger, modell og sesong kan påvirke leveringstiden. Vi oppgir alltid en estimert leveringsdato i tilbudet.',
    },
    {
      q: 'Trenger jeg byggetillatelse for en badstue?',
      a: 'Badstuer under 15 m² byggn filingsfri, men du må likevel sende byggesøknad (ansvarsrett). For enheter over 15 m² eller over 3 m høyde kreves vanligvis byggetillatelse. Vi anbefaler alltid å sjekke med kommunen før oppføring.',
    },
    {
      q: 'Hvor lang tid tar monteringen?',
      a: 'Montering av en ferdig badstue tar typisk 1–2 dager med vårt monteringsteam. Grunnarbeid og eventuell fundamentering kommer i tillegg.',
    },
    {
      q: 'Hva koster en LYKKE badstue?',
      a: 'Pris avhenger av modell, størrelse og tilpasninger. Våre badstuer ligger typisk i intervallet NOK 150 000 – 600 000. Bruk konfiguratoren for å få et nøyaktig prisestimat.',
    },
    {
      q: 'Leverer dere over hele Norge?',
      a: 'Ja, vi leverer og monterer badstuer over hele Norge. Transportkostnad avhenger av avstand fra fabrikk til installasjonssted.',
    },
    {
      q: 'Hvilken garanti gir dere på badstuen?',
      a: 'Vi gir 2 års garanti på badstuen, og opptil 10 års garanti visse komponenter (f.eks. Kebony-kledning). Garantibetingelser oppgis i kontrakt.',
    },
  ];

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: qa.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };
}

/**
 * Product schema dla stron sauna-* / konfiguratorów.
 * Asynchronicznie wzbogacany o zdjęcie i cenę (NOK) z ProductCatalogItem / SaunaPrice.
 */
export async function getProductSchema(pathname) {
  const info = getSaunaModel(pathname);
  if (!info) return null;

  const seo = getSeoConfig(pathname);
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: info.name,
    description: seo.description,
    brand: { '@type': 'Brand', name: 'LYKKE NOMAD' },
    category: 'Badstue',
    sku: info.model,
    url: SITE + pathname,
  };

  let image = null;
  let price = null;
  let currency = 'NOK';

  // 1) Produkt z katalogu (zdjęcie + cena bazowa)
  try {
    const items = await base44.entities.ProductCatalogItem.filter({
      model: info.model,
      isPublished: true,
    });
    const item = items && items[0];
    if (item) {
      image = item.mainImageUrl || null;
      if (item.basePrice) {
        price = item.basePrice;
        currency = item.currency || 'NOK';
      }
    }
  } catch (e) {
    /* ignoruj – brak danych katalogowych */
  }

  // 2) Fallback: SaunaPrice (NOK)
  if (!price) {
    try {
      const prices = await base44.entities.SaunaPrice.filter({ saunaId: info.model });
      const p = prices && prices[0];
      if (p && p.price) {
        price = p.price;
        currency = 'NOK';
      }
    } catch (e) {
      /* ignoruj */
    }
  }

  if (image) schema.image = image;

  const offers = {
    '@type': 'Offer',
    priceCurrency: currency,
    availability: 'https://schema.org/InStock',
    url: SITE + pathname,
    seller: { '@type': 'Organization', name: 'Lykke Home AS' },
  };
  if (price != null) offers.price = String(price);
  schema.offers = offers;

  return schema;
}